import React from 'react'
import { gql } from '@apollo/client'
import map from 'lodash/map'
import type { Order, RouteDef } from '@ztechapps/common.contract'
import type { OrderCodeLinkProps } from '@ztechapps/webui.contract'
import { useDocument } from '@ztechapps/common.hook'
import { getShippingDateLabel } from '@ztechapps/order.common'
import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
import { LocalDate, LocalDateTime } from '@ztechapps/webui.date'
import { formatMoney, getCompanyOrProfileName } from '@ztechapps/common.utils'
import { EntityCodeLink, EntityPreview, ShowIf } from '@ztechapps/webui.util'
import { OrderStatus, OrderStatusBadge } from '../StatusBadge'

const orderTooltipQuery = gql`
  query singleOrder($filter: JSON) {
    singleOrder(filter: $filter) {
      _id
      code
      status
      customerPoNumber
      createdAt
      shipping {
        _id
        date
      }
      net
      gross
      by {
        _id
        company {
          _id
          name
        }
        profile {
          _id
          name
        }
      }
      items {
        _id
        product {
          _id
          code
        }
      }
    }
  }
`

const OrderPreview = ({ _id, code }: { _id: string; code: string }) => {
  const orderDocumentResult = useDocument<Order>({ _id, query: orderTooltipQuery })
  const order = orderDocumentResult?.document

  return (
    <EntityPreview
      code={code}
      statusBadge={<OrderStatusBadge status={order?.status} />}
      loading={orderDocumentResult.loading}
      error={orderDocumentResult.error}
      title={getCompanyOrProfileName(order?.by)}
      rows={[
        { label: 'Customer PO #', value: order?.customerPoNumber },
        // `LocalDateTime` always renders SOMETHING (its own "--" for a missing date), so
        // `EntityPreview`'s `?? '---'` fallback never sees a falsy value to catch — checked here
        // instead, same as `WorkOrderCodeLink`'s Tags row.
        { label: 'Created', value: order?.createdAt ? <LocalDateTime value={order.createdAt} /> : undefined },
        {
          label: getShippingDateLabel(order?.status),
          value: order?.shipping?.date ? <LocalDate value={order.shipping.date} /> : undefined,
        },
        { label: 'Net', value: formatMoney(order?.net) },
        { label: 'Gross Amount', value: formatMoney(order?.gross) },
      ]}
      list={{ label: 'Items', items: map(order?.items, (item) => item?.product?.code) }}
    />
  )
}

export const OrderCodeLink = ({ _id, code, status, target }: OrderCodeLinkProps) => {
  const orderItemRoute = useInject<RouteDef>(WebuiIocKey.routes.orderItem)

  return (
    <EntityCodeLink
      code={code}
      statusDot={
        <ShowIf condition={!!status}>
          <OrderStatus status={status} />
        </ShowIf>
      }
      to={orderItemRoute.altPath({ _id })}
      target={target}
      preview={<OrderPreview _id={_id} code={code} />}
    />
  )
}
